import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { cn } from "@/lib/utils";
import SEO from "@/components/SEO";

const PRIMARY_CTA_URL =
  "https://greyrockai.app/?utm_source=seo&utm_medium=landing&utm_campaign=ai-detect-narcissistic-abuse";
const SECONDARY_CTA_URL =
  "https://greyrockai.app/?utm_source=seo&utm_medium=landing&utm_campaign=ai-detect-narcissistic-abuse-bottom";

// Same styling as /grey-rock-method-examples CTAs.
const ctaClass = cn(
  "inline-flex items-center justify-center gap-2 px-8 h-12 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black text-sm font-semibold tracking-wide transition-all",
  "shadow-[0_0_40px_-6px_rgba(34,211,238,0.85)] hover:shadow-[0_0_60px_-4px_rgba(34,211,238,1)] animate-cyanPulse"
);

const FAQ = [
  {
    q: "Is GreyRock AI based on the Huddersfield DCAP research?",
    a: "No. GreyRock AI is not affiliated with the University of Huddersfield and was not built using the DCAP framework. Both tools belong to the same broader category of technology — forensic text analysis to identify manipulation patterns in conversations — but they are independent projects. The Huddersfield research validates the category. GreyRock makes the category accessible for personal use.",
  },
  {
    q: "Can AI really detect manipulation in text messages?",
    a: "The peer-reviewed research says yes, when the AI is designed for the specific task. General-purpose language models miss psychological abuse because they're trained to identify overt aggression. Forensic tools like DCAP — and consumer tools like GreyRock — are specifically trained on the linguistic markers of manipulation, gaslighting, isolation, and coercive control. The detection works because the pattern is real, and the pattern leaves a signature in language.",
  },
  {
    q: "How accurate is AI detection of narcissistic abuse?",
    a: "The Huddersfield team reported that DCAP reduced an 8,400-message simulated case down to 287 messages containing the most prevalent abusive markers — over 92% reduction in manual review workload, with the flagged messages independently verified by human analysts as containing the relevant patterns. Consumer tools like GreyRock use similar principles applied to shorter conversations. No AI system is 100% accurate, and the output should be treated as analysis to consider rather than a clinical diagnosis. The accuracy that matters is whether the analysis surfaces patterns the survivor couldn't see on their own — and on that measure, forensic text analysis consistently outperforms reading conversations one message at a time.",
  },
  {
    q: "Can I use AI analysis of texts as legal evidence?",
    a: "This depends on your jurisdiction and the specific legal context. The Huddersfield DCAP framework was designed with forensic admissibility in mind and is intended for use by trained investigators in legal proceedings. Consumer tools like GreyRock are designed for personal understanding, not legal evidence. If you need analysis for a legal case, consult an attorney about whether forensic text analysis is admissible in your jurisdiction and what professional services can produce admissible reports.",
  },
  {
    q: "Why can't I see the manipulation patterns myself?",
    a: "The research answers this directly. Psychological abuse is cumulative — it lives in patterns across hundreds of messages, not in individual messages. The human brain reads conversations message by message, in emotional context, often after being conditioned by the abuser to minimize what's happening. That's not a failure of intelligence. It's a structural limitation of how humans read text. A forensic tool reads the whole record at once, without emotional context, and surfaces the pattern as data. That's what makes the pattern visible.",
  },
];

const Blockquote = ({ children }: { children: React.ReactNode }) => (
  <blockquote className="my-4 border-l-2 border-cyan-400/50 bg-white/[0.03] px-5 py-3 rounded-r-md text-white/80 italic font-serif text-[17px] leading-relaxed">
    {children}
  </blockquote>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75 mt-4">{children}</p>
);

const AiDetectNarcissisticAbuse = () => {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div
      className="min-h-screen bg-black text-white"
      style={{
        background:
          "radial-gradient(ellipse at top, rgba(34,211,238,0.06) 0%, rgba(0,0,0,1) 60%)",
      }}
    >
      <SEO
        title="Can AI Detect Narcissistic Abuse in Texts? The Research Says Yes | GreyRock AI"
        description="Researchers built an AI to detect narcissistic abuse patterns in text messages — and it worked. Here's what the science found, and how you can apply the same forensic analysis to your own conversations."
        path="/ai-detect-narcissistic-abuse"
      />
      <Helmet>
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      {/* Slim top bar */}
      <header className="px-5 py-4 border-b border-white/[0.06]">
        <div className="max-w-[720px] mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="text-[11px] font-mono uppercase tracking-[0.25em] text-cyan-300/80 hover:text-cyan-200 transition-colors"
          >
            ← GreyRock AI®
          </Link>
          <a
            href={PRIMARY_CTA_URL}
            className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/55 hover:text-cyan-200 transition-colors"
          >
            Try the analyzer →
          </a>
        </div>
      </header>

      <main className="px-5 py-12 md:py-20">
        <article className="max-w-[720px] mx-auto">
          {/* Section 1 — Hero */}
          <h1 className="text-[2rem] md:text-[3rem] font-semibold tracking-tight leading-[1.1] mb-8">
            Can AI Detect Narcissistic Abuse in Texts?
          </h1>
          <p className="text-[18px] md:text-[19px] leading-[1.7] text-white/80">
            A research team at the University of Huddersfield just published a peer-reviewed paper
            showing it can.
          </p>
          <P>
            Their tool — called the Digital Conversation Analysis Pipeline, or DCAP — reads through
            text message conversations and identifies the linguistic patterns of psychological
            manipulation, gaslighting, and coercive control. In a simulated forensic case, it
            analyzed 8,400 messages and flagged 287 as evidence of abuse. The other 8,113 were
            noise.
          </P>
          <P>
            That's the part that matters. The abuse wasn't in every message. It was in 3.4% of
            them. That's why survivors doubt themselves — they're reading conversations one message
            at a time, in emotional context, after being conditioned to minimize. The pattern only
            emerges when something analyzes the whole conversation as a forensic record.
          </P>
          <P>
            This page explains what the research found, why it matters for survivors, and how the
            same forensic principle is now accessible without a research lab.
          </P>
          <P>
            Read more:{" "}
            <a href="https://medium.com/p/ebb57e0abc6b" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline underline-offset-4 hover:text-cyan-300 transition-colors">
              What Forensic Text Analysis Reveals About Narcissistic Abuse
            </a>
          </P>

          {/* Section 2 */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              What the Huddersfield research actually built
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              The team — PhD researcher Dhruv Patel and senior lecturer Dr. Anju Johnson at the
              University of Huddersfield's School of Computing and Engineering — set out to solve a
              specific problem in digital forensic investigations.
            </p>
            <P>
              Standard keyword searches miss psychological abuse. There's no single word that flags
              it. Gaslighting, isolation, conditional affection, retroactive reframes — these don't
              show up in a search for "abuse" or "manipulation." They show up in the{" "}
              <em>cumulative</em> pattern of language across hundreds or thousands of messages.
            </P>
            <P>
              DCAP is what the researchers call a hybrid AI approach. It combines forensic keyword
              analysis with a deep learning model that understands context — sarcasm, modern slang,
              manipulation disguised as concern. The system was specifically built to scan for the
              nine diagnostic criteria of Narcissistic Personality Disorder, the way they appear in
              linguistic patterns.
            </P>
            <P>
              Their findings were published in March 2026 in two peer-reviewed venues:{" "}
              <em>Forensic Science International: Digital Investigation</em> and <em>IEEE Access</em>.
              The research is real. The category is real. AI-driven detection of narcissistic abuse
              patterns is no longer theoretical — it's published science.
            </P>
          </section>

          {/* Section 3 */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              Why this matters: abuse is cumulative, not episodic
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              The most important finding isn't the AI itself. It's what the AI's success reveals
              about the nature of psychological abuse.
            </p>
            <P>
              A single message looks fine. <em>"You're being too sensitive."</em> A single sentence
              isn't abuse. It's a comment. Maybe even a fair one, depending on context.
            </P>
            <P>
              Two hundred messages, each one labeling your reactions as the problem, is something
              else entirely. It's a pattern. And the pattern is the evidence.
            </P>
            <P>
              This is the gap the research exposes. Survivors of narcissistic abuse don't doubt
              themselves because they're irrational. They doubt themselves because each individual
              interaction looks defensible in isolation. Their nervous system registers the
              cumulative damage long before their conscious mind can articulate it. Their body
              knows. Their thinking mind can't find the evidence.
            </P>
            <P>
              A forensic tool that analyzes the whole conversation as a record solves that gap. It
              surfaces the pattern the survivor couldn't quite name. It puts language on what the
              body already knew.
            </P>
            <P>
              That's why the Huddersfield team built DCAP for forensic investigators — and that's
              why the same approach matters for anyone trying to make sense of what's been
              happening to them.
            </P>
          </section>

          {/* Section 4 */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              What the AI looked for
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              DCAP scans for specific linguistic markers tied to the diagnostic criteria of
              Narcissistic Personality Disorder. The markers include:
            </p>

            <P>
              <strong className="text-white font-semibold">Sarcasm disguised as concern.</strong>
            </P>
            <Blockquote>"Wow, must be nice to have all that free time."</Blockquote>
            <P>The form is curiosity. The function is contempt.</P>

            <P>
              <strong className="text-white font-semibold">
                Isolation embedded in ordinary language.
              </strong>
            </P>
            <Blockquote>"Your friends don't really care about you the way I do."</Blockquote>
            <P>The sentence sounds like loyalty. The function is to weaken outside relationships.</P>

            <P>
              <strong className="text-white font-semibold">
                Cumulative humiliation framed as humor.
              </strong>
            </P>
            <Blockquote>"It was just a joke."</Blockquote>
            <P>
              A single instance reads as harmless. Forty instances across six months read as a
              pattern of contempt with built-in deniability.
            </P>

            <P>
              <strong className="text-white font-semibold">Retroactive reframes.</strong>
            </P>
            <Blockquote>"That's not what I said."</Blockquote>
            <P>
              Each occurrence destabilizes the survivor's memory of what happened. Over time, the
              cumulative effect is that the survivor stops trusting their own recall.
            </P>

            <P>
              <strong className="text-white font-semibold">Conditional affection markers.</strong>
            </P>
            <Blockquote>"If you really loved me, you would…"</Blockquote>
            <P>
              Love is converted into a measurement test. Compliance is proof of love. Refusal is
              proof of betrayal.
            </P>

            <P>
              These markers don't matter individually. They matter as a pattern across the full
              record of communication. That's the <em>forensic</em> insight.
            </P>
          </section>

          {/* Section 5 + mid CTA */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              The gap between research and reality
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              DCAP is research-grade. It's designed for forensic investigators working legal cases —
              typically with court orders, full message extraction, and trained analysts
              interpreting the output.
            </p>
            <P>Most survivors don't have access to that.</P>
            <P>
              The person who's questioning their memory on a Tuesday night, reading back through
              old texts trying to figure out whether what they remembered actually happened, doesn't
              have a forensics lab. They have a phone, a conversation, and a nervous system that
              keeps telling them something is wrong while their mind keeps generating reasons it
              might not be.
            </P>
            <P>
              GreyRock AI was built to close that gap. It's the same category of technology —
              forensic text analysis to detect manipulation patterns — designed for personal use
              rather than legal investigation. You paste in a conversation. It identifies the
              tactics being used, scores the manipulation patterns it detects, and shows you which
              messages carry the markers.
            </P>
            <P>
              It's not a clinical tool. It's not a diagnosis. It's not affiliated with the
              Huddersfield research. What it shares with DCAP is the underlying principle: that
              psychological abuse leaves a forensic signature in text, and that signature can be
              detected when something analyzes the whole pattern instead of single moments.
            </P>

            <div className="mt-8 text-center">
              <a href={PRIMARY_CTA_URL} className={ctaClass}>
                Run a Forensic Analysis on Your Texts →
              </a>
            </div>

            <p className="text-[15px] md:text-[16px] leading-[1.7] text-white/55 mt-6 text-center max-w-lg mx-auto">
              Three free analyses to see how the scoring works. No signup required to start.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              How to apply forensic thinking to your own conversations
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              The Huddersfield research validates a principle anyone can apply, with or without an
              AI tool. Forensic thinking about text conversations means:
            </p>

            <P>
              <strong className="text-white font-semibold">Read the pattern, not the moments.</strong>{" "}
              Individual messages will always have alternative interpretations. The pattern across
              hundreds of messages does not. If you find yourself defending or explaining away the
              same kind of message over and over, that repetition itself is the evidence.
            </P>
            <P>
              <strong className="text-white font-semibold">Look for cumulative markers.</strong> One
              sarcastic comment is a bad day. Sixty sarcastic comments across two years, all
              directed at the same kinds of topics, is a pattern of contempt. The frequency and
              consistency are diagnostic.
            </P>
            <P>
              <strong className="text-white font-semibold">
                Trust the body's registration before the mind's analysis.
              </strong>{" "}
              If a message makes your stomach drop and then your mind generates seven reasons it
              shouldn't have, the stomach drop is the more reliable signal. The mind has been
              trained to minimize. The body has not.
            </P>
            <P>
              <strong className="text-white font-semibold">
                Treat the conversation as a record, not a relationship.
              </strong>{" "}
              This is the hardest reframe. You stop reading the texts as the person you love or
              fear or are trying to understand. You read them as evidence. The forensic distance is
              what lets the pattern become visible.
            </P>
            <P>
              This is what GreyRock automates. The scoring system applies these forensic principles
              across the entire conversation at once. It surfaces the pattern the way DCAP surfaces
              patterns for forensic investigators — but for the conversation in your own phone, on
              your own schedule, when you need to make sense of what's been happening.
            </P>
          </section>

          {/* Section 7 — FAQ */}
          <section className="mt-14">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-6">
              Common questions about AI detection of narcissistic abuse
            </h2>
            {FAQ.map((f) => (
              <div key={f.q} className="mt-8 first:mt-0">
                <h3 className="text-[17px] md:text-[19px] font-semibold tracking-tight text-white mb-3 leading-snug">
                  {f.q}
                </h3>
                <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">{f.a}</p>
              </div>
            ))}
          </section>

          {/* Section 8 — soft close */}
          <section className="mt-16">
            <h2 className="text-[1.5rem] md:text-[1.875rem] font-semibold tracking-tight leading-[1.2] mb-5">
              The forensic frame
            </h2>
            <p className="text-[17px] md:text-[18px] leading-[1.7] text-white/75">
              The Huddersfield research matters because it validates a category that survivors have
              been trying to articulate for years. What was once dismissed as oversensitivity or
              paranoia is now the subject of peer-reviewed forensic science. The patterns are real.
              The markers are detectable. The evidence is in the conversation.
            </p>
            <P>
              GreyRock AI exists to make that detection accessible — not as a research instrument,
              but as a personal tool for the moments when you need to make sense of what's actually
              happening in your own messages. It's part of{" "}
              <Link
                to="/articulation-before-adjudication"
                className="text-cyan-300/90 hover:text-cyan-200 underline-offset-4 hover:underline transition-colors"
              >
                the emerging category these tools belong to →
              </Link>
            </P>

            <div className="mt-8 text-center">
              <a href={SECONDARY_CTA_URL} className={ctaClass}>
                Analyze Your Conversation →
              </a>
            </div>

            {/* Related */}
            <div className="mt-14 pt-6 border-t border-white/[0.06] text-center">
              <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-2">
                Related
              </p>
              <Link
                to="/grey-rock-method-examples"
                className="text-[14px] md:text-[15px] text-cyan-300/85 hover:text-cyan-200 transition-colors underline-offset-4 hover:underline"
              >
                Read: Grey Rock Method — Real Examples From Real Conversations →
              </Link>
            </div>
          </section>
        </article>
      </main>

      <footer className="border-t border-cyan-400/10 mt-16 py-6 px-5 text-center text-[11px] font-mono text-white/30 tracking-wider">
        <div>GREYROCKAI® · BEHAVIORAL INTELLIGENCE</div>
        <div className="mt-1.5 flex items-center justify-center gap-4">
          <Link to="/" className="hover:text-cyan-300/80 transition-colors">Home</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-cyan-300/80 transition-colors">Privacy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-cyan-300/80 transition-colors">Terms</Link>
          <span>·</span>
          <Link to="/contact" className="hover:text-cyan-300/80 transition-colors">Contact</Link>
        </div>
      </footer>

      <style>{`
        @keyframes cyanPulseLocal {
          0%, 100% { box-shadow: 0 0 40px -6px rgba(34,211,238,0.7); }
          50% { box-shadow: 0 0 60px -2px rgba(34,211,238,1); }
        }
        .animate-cyanPulse { animation: cyanPulseLocal 3.2s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

export default AiDetectNarcissisticAbuse;
